<template>
  <div class="login-scene flex min-h-screen items-center justify-center px-4 py-8 md:px-6">
    <div class="grid w-full max-w-7xl gap-6 lg:grid-cols-[1.18fr_0.82fr]">
      <section class="relative overflow-hidden rounded-[2.8rem] bg-slate-950 px-8 py-10 text-white shadow-panel md:px-12 md:py-12">
        <img
          src="https://images.unsplash.com/photo-1650337771276-1ff47d60a7df?auto=format&fit=crop&w=1600&q=80"
          alt="Unsplash workspace"
          class="absolute inset-0 h-full w-full object-cover"
        >
        <div class="absolute inset-0 bg-[linear-gradient(130deg,rgba(5,10,18,0.9),rgba(8,15,23,0.62),rgba(21,61,61,0.56))]"></div>
        <div class="absolute inset-0 bg-[radial-gradient(circle_at_16%_18%,rgba(255,255,255,0.16),transparent_22%),radial-gradient(circle_at_76%_24%,rgba(185,111,72,0.16),transparent_22%)]"></div>
        <div class="absolute -left-16 top-10 h-48 w-48 rounded-full border border-white/10"></div>
        <div class="absolute bottom-8 right-8 h-32 w-32 rounded-[2rem] border border-white/10"></div>
        <div class="relative">
          <div class="text-[0.72rem] uppercase tracking-[0.5em] text-white/32">Etsy Workspace</div>
          <div class="mt-6 flex items-center gap-5">
            <div class="brand-mark brand-mark--hero">
              <span class="brand-mark__ring"></span>
              <span class="brand-mark__core">E</span>
            </div>
            <div>
              <h1 class="font-display text-6xl font-black tracking-[0.18em] text-white md:text-7xl">E品通</h1>
              <div class="mt-2 text-[0.72rem] uppercase tracking-[0.42em] text-white/44">Signature Product Studio</div>
            </div>
          </div>
          <h2 class="mt-8 max-w-2xl font-display text-3xl font-bold leading-tight text-white/96 md:text-5xl">
            让产品生成、图片管理与 Etsy 导出集中在一个入口。
          </h2>
          <p class="mt-6 max-w-lg text-sm leading-7 text-white/48 md:text-base">
            登录后即可开始录入产品、维护提示词、生成图文并批量导出官方 CSV。
          </p>
          <div class="mt-10 grid gap-4 md:grid-cols-3">
            <div class="rounded-[1.6rem] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
              <div class="text-[0.78rem] uppercase tracking-[0.22em] text-white/38">管理员</div>
              <div class="mt-3 text-xl font-semibold">admin</div>
              <div class="mt-1 text-sm text-white/62">admin123</div>
            </div>
            <div class="rounded-[1.6rem] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
              <div class="text-[0.78rem] uppercase tracking-[0.22em] text-white/38">运营账号</div>
              <div class="mt-3 text-xl font-semibold">operator</div>
              <div class="mt-1 text-sm text-white/62">operator123</div>
            </div>
            <div class="rounded-[1.6rem] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
              <div class="text-[0.78rem] uppercase tracking-[0.22em] text-white/38">输出结果</div>
              <div class="mt-3 text-xl font-semibold">Etsy CSV</div>
              <div class="mt-1 text-sm text-white/62">可直接上传</div>
            </div>
          </div>
          <div class="mt-8 text-[11px] tracking-[0.16em] text-white/28">
            Background image from Unsplash
          </div>
        </div>
      </section>

      <section class="glass-panel login-card rounded-[2.8rem] p-8 md:p-10">
        <div class="mb-8">
          <div class="text-[0.72rem] uppercase tracking-[0.4em] text-slate-400">Sign In</div>
          <div class="mt-4 flex items-center gap-3">
            <div class="brand-mark brand-mark--small">
              <span class="brand-mark__ring"></span>
              <span class="brand-mark__core">E</span>
            </div>
            <div class="font-display text-4xl font-black tracking-[0.14em] text-brand-teal">E品通</div>
          </div>
          <h2 class="mt-3 text-3xl font-bold text-slate-900">登录系统</h2>
          <p class="mt-3 text-sm leading-6 text-slate-400">
            输入账号后进入工作台。
          </p>
        </div>

        <form class="space-y-5" @submit.prevent="handleLogin">
          <div>
            <label class="field-label">用户名</label>
            <input v-model.trim="form.username" class="field-input" placeholder="请输入用户名">
          </div>
          <div>
            <label class="field-label">密码</label>
            <input v-model.trim="form.password" type="password" class="field-input" placeholder="请输入密码">
          </div>
          <div class="rounded-[1.6rem] border border-brand-teal/10 bg-brand-teal/5 px-4 py-4 text-sm leading-6 text-slate-500">
            首次启动后会自动初始化默认账户和基础提示词模板。
          </div>
          <button class="btn btn-primary w-full" :disabled="loading">
            {{ loading ? '登录中...' : '进入 E品通' }}
          </button>
        </form>
      </section>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { authApi } from '@/api';
import { setToken, setUser } from '@/utils/auth';
import { notifyError } from '@/utils/feedback';

const router = useRouter();
const loading = ref(false);
const form = reactive({
  username: 'admin',
  password: 'admin123'
});

async function handleLogin() {
  if (!form.username || !form.password) {
    notifyError('请输入用户名和密码');
    return;
  }
  loading.value = true;
  try {
    const res = await authApi.login(form);
    setToken(res.data.token);
    setUser(res.data);
    router.push('/dashboard');
  } catch (error) {
    notifyError(error.message || '登录失败');
  } finally {
    loading.value = false;
  }
}
</script>
